import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  Grid,
  useTheme,
  alpha,
  Alert
} from '@mui/material';
import { 
  useNavigate, 
  useLocation 
} from 'react-router-dom';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AppError } from '../utils/errorHandler';

const Error500: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();

  // Extrair erro do state de navegação
  const errorState = location.state as { error?: AppError } | undefined;
  const error = errorState?.error;

  const handleGoHome = () => {
    if (isAuthenticated && user) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            width: '100%',
            maxWidth: 700
          }}
        >
          {/* Ícone e número 500 */}
          <Box sx={{ mb: 4 }}>
            <WarningIcon 
              sx={{ 
                fontSize: 120, 
                color: theme.palette.error.main,
                mb: 2,
                animation: 'pulse 2s infinite'
              }} 
            />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '4rem', md: '6rem' },
                fontWeight: 'bold',
                color: theme.palette.error.main,
                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
            >
              500
            </Typography>
          </Box>

          {/* Mensagem principal */}
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2
            }}
          >
            Erro Interno do Servidor
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ fontSize: '1.1rem', mb: 4 }}
          >
            Ocorreu um erro inesperado no servidor. Nossa equipe foi notificada e está trabalhando para resolver o problema.
            <br />
            Por favor, tente novamente em alguns instantes.
          </Typography>

          {/* Detalhes do erro (apenas em desenvolvimento ou se disponível) */}
          {(process.env.NODE_ENV === 'development' || error) && error && (
            <Alert 
              severity="error" 
              icon={<BugReportIcon />}
              sx={{ 
                mb: 4,
                textAlign: 'left',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Detalhes do Erro:
              </Typography>
              {error.message && (
                <Typography variant="body2" component="div" gutterBottom>
                  <strong>Mensagem:</strong> <code style={{ 
                    backgroundColor: 'rgba(0,0,0,0.05)', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>{error.message}</code>
                </Typography>
              )}
              {error.status && (
                <Typography variant="body2" component="div" gutterBottom>
                  <strong>Status:</strong> <code style={{ 
                    backgroundColor: 'rgba(0,0,0,0.05)', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>{error.status}</code>
                </Typography>
              )}
              {error.code && (
                <Typography variant="body2" component="div" gutterBottom>
                  <strong>Código:</strong> <code style={{ 
                    backgroundColor: 'rgba(0,0,0,0.05)', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>{error.code}</code>
                </Typography>
              )}
              {error.details && process.env.NODE_ENV === 'development' && (
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  <strong>Stack:</strong>
                  <Box 
                    component="pre" 
                    sx={{ 
                      mt: 1, 
                      p: 1, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 200
                    }}
                  >
                    {error.details}
                  </Box>
                </Typography>
              )}
            </Alert>
          )}

          {/* Botões de ação */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: theme.shadows[4]
                }}
              >
                Ir para o Início
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Voltar
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                size="large"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Recarregar Página
              </Button>
            </Grid>
          </Grid>

          {/* Informações úteis */}
          <Box 
            sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              textAlign: 'left'
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              O que você pode fazer:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 0 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Aguarde alguns instantes e tente novamente
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Verifique sua conexão com a internet
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Se o problema persistir, entre em contato com o suporte
                </Typography>
              </li>
            </Box>
          </Box>

          {/* Informações do usuário (se logado) */}
          {isAuthenticated && user && (
            <Box 
              sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` 
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Logado como: <strong>{user.name}</strong> ({user.role})
              </Typography>
            </Box>
          )}
        </Paper>

        {/* CSS para animação */}
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.1); opacity: 0.8; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}
        </style>
      </Box>
    </Container>
  );
};

export default Error500;

